import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import '../../v2-theme.css';

function CheckoutRestaurantUnlimited() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [contactName, setContactName] = useState('');

  const pricing = {
    monthly: { amount: 99, display: '$99/month', priceId: process.env.REACT_APP_STRIPE_RESTAURANT_MONTHLY_PRICE_ID },
    annual: { amount: 999, display: '$999/year', priceId: process.env.REACT_APP_STRIPE_RESTAURANT_ANNUAL_PRICE_ID }
  };

  const currentPrice = pricing[billingCycle];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Step 1: Create payment method
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name: contactName,
          email,
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Step 2: Send payment method to server to create subscription
      const { data } = await api.post('/subscriptions/create-subscription', {
        paymentMethodId: paymentMethod.id,
        email,
        name: `${businessName} - ${contactName}`,
        plan: 'restaurant_unlimited',
        billingCycle,
        priceId: currentPrice.priceId
      });

      // Step 3: Handle subscription response
      if (data.clientSecret) {
        // Requires additional authentication (3D Secure)
        const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);

        if (confirmError) {
          throw new Error(confirmError.message);
        }
      }

      // Success! Redirect to success page
      navigate('/payment-success', {
        state: {
          type: 'restaurant_unlimited',
          amount: currentPrice.amount,
          billingCycle,
          message: 'Welcome to Unlimited! You can now post unlimited shifts.'
        }
      });
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.response?.data?.error || err.message || 'Subscription failed');
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        color: '#FFFFFF',
        fontFamily: '"Work Sans", sans-serif',
        fontSize: '16px',
        '::placeholder': {
          color: '#C0C0C0',
        },
      },
      invalid: {
        color: '#fa755a',
      },
    },
  };

  return (
    <div className="v2-page-wrapper">
      <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <div className="v2-header-center" style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '20px' }}>
            <img src="/hotf-web-icon.png" alt="Hands On The Fly" style={{ width: '50px', height: '50px' }} />
            <h1 style={{ fontFamily: 'Times New Roman, serif', fontSize: '1.8rem', fontWeight: 'normal' }}>
              Hands On The Fly
            </h1>
          </div>
        </div>

        <h2 className="v2-heading-xl" style={{ textAlign: 'center', marginBottom: '10px' }}>
          Upgrade to Unlimited
        </h2>
        <p className="v2-text-muted" style={{ textAlign: 'center', marginBottom: '40px' }}>
          Post unlimited shifts and maximize your staffing efficiency
        </p>

        {/* Billing Cycle Toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '40px', alignItems: 'center' }}>
          <button
            onClick={() => setBillingCycle('monthly')}
            style={{
              padding: '12px 30px',
              background: billingCycle === 'monthly' ? 'var(--steel)' : 'rgba(255, 255, 255, 0.05)',
              border: `2px solid ${billingCycle === 'monthly' ? 'var(--steel)' : 'var(--dark-steel)'}`,
              color: billingCycle === 'monthly' ? 'var(--charcoal)' : 'var(--silver)',
              fontFamily: 'Work Sans, sans-serif',
              fontSize: '0.95rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontWeight: '600'
            }}
          >
            Monthly - $99
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            style={{
              padding: '12px 30px',
              background: billingCycle === 'annual' ? 'var(--steel)' : 'rgba(255, 255, 255, 0.05)',
              border: `2px solid ${billingCycle === 'annual' ? 'var(--steel)' : 'var(--dark-steel)'}`,
              color: billingCycle === 'annual' ? 'var(--charcoal)' : 'var(--silver)',
              fontFamily: 'Work Sans, sans-serif',
              fontSize: '0.95rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontWeight: '600'
            }}
          >
            Annual - $999 {' '}
            <span style={{
              background: 'var(--steel)',
              color: 'var(--charcoal)',
              padding: '4px 10px',
              fontSize: '0.75rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Save $189
            </span>
          </button>
        </div>

        {/* Checkout Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '30px' }}>
          {/* Plan Summary */}
          <div className="v2-card" style={{ padding: '30px', border: '2px solid var(--steel)' }}>
            <div style={{
              background: 'var(--steel)',
              color: 'var(--charcoal)',
              padding: '6px 14px',
              fontSize: '0.75rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              display: 'inline-block',
              marginBottom: '20px'
            }}>
              Restaurant Unlimited
            </div>

            <div className="v2-heading-lg" style={{ marginBottom: '15px' }}>
              Unlimited
            </div>

            <div style={{ fontSize: '2.5rem', fontWeight: '600', marginBottom: '10px' }}>
              {billingCycle === 'monthly' ? '$99' : '$999'}
              <span style={{ fontSize: '1rem', color: 'var(--silver)', fontWeight: '400' }}>
                /{billingCycle === 'monthly' ? 'month' : 'year'}
              </span>
            </div>

            <div style={{ fontSize: '0.85rem', color: 'var(--silver)', marginBottom: '20px' }}>
              {billingCycle === 'monthly' ? 'Billed monthly • Cancel anytime' : 'Billed annually • Save $189/year • Cancel anytime'}
            </div>

            <ul style={{ listStyle: 'none', marginBottom: '20px' }}>
              {[
                'Unlimited shift postings',
                'No per-shift fees',
                'Enhanced restaurant profile',
                'Priority listing in worker searches',
                'Advanced analytics dashboard',
                'Dedicated support',
                'Early access to new features'
              ].map((feature, index) => (
                <li key={index} style={{
                  padding: '10px 0',
                  borderBottom: '1px solid var(--dark-steel)',
                  fontSize: '0.9rem'
                }}>
                  <span style={{ color: 'var(--steel)', marginRight: '10px' }}>✓</span>
                  {feature}
                </li>
              ))}
            </ul>

            <div style={{
              padding: '15px',
              background: 'rgba(232, 232, 232, 0.1)',
              borderLeft: '3px solid var(--steel)',
              marginTop: '20px'
            }}>
              <strong>ROI Calculator:</strong><br />
              Post 6+ shifts/month? Unlimited pays for itself.<br />
              Average restaurant posts 12 shifts/month = <strong>$129 savings</strong>
            </div>
          </div>

          {/* Payment Form */}
          <div className="v2-card" style={{ padding: '30px' }}>
            <form onSubmit={handleSubmit}>
              {/* Business Information */}
              <div style={{ marginBottom: '30px' }}>
                <div className="v2-heading-md" style={{ marginBottom: '15px' }}>
                  Business Information
                </div>

                <div className="v2-form-group">
                  <label className="v2-label">Business Name</label>
                  <input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    required
                    placeholder="The Steel Kitchen"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--charcoal)',
                      border: '1px solid var(--dark-steel)',
                      color: 'var(--white)',
                      fontFamily: 'Work Sans, sans-serif',
                      fontSize: '1rem',
                      borderRadius: '2px'
                    }}
                  />
                </div>

                <div className="v2-form-group">
                  <label className="v2-label">Business Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="manager@steelkitchen.com"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--charcoal)',
                      border: '1px solid var(--dark-steel)',
                      color: 'var(--white)',
                      fontFamily: 'Work Sans, sans-serif',
                      fontSize: '1rem',
                      borderRadius: '2px'
                    }}
                  />
                </div>

                <div className="v2-form-group">
                  <label className="v2-label">Contact Name</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    required
                    placeholder="Sarah Johnson"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--charcoal)',
                      border: '1px solid var(--dark-steel)',
                      color: 'var(--white)',
                      fontFamily: 'Work Sans, sans-serif',
                      fontSize: '1rem',
                      borderRadius: '2px'
                    }}
                  />
                </div>
              </div>

              {/* Payment Information */}
              <div style={{ marginBottom: '30px' }}>
                <div className="v2-heading-md" style={{ marginBottom: '15px' }}>
                  Payment Information
                </div>

                <div className="v2-form-group">
                  <label className="v2-label">Card Details</label>
                  <div style={{
                    padding: '12px',
                    background: 'var(--charcoal)',
                    border: '1px solid var(--dark-steel)',
                    borderRadius: '2px'
                  }}>
                    <CardElement options={cardElementOptions} />
                  </div>
                  {error && (
                    <div style={{ color: '#fa755a', fontSize: '0.85rem', marginTop: '8px' }}>
                      {error}
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                className="v2-btn v2-btn-primary"
                disabled={!stripe || loading}
                style={{ width: '100%', marginTop: '10px' }}
              >
                {loading ? 'Processing...' : `Subscribe Now - ${currentPrice.display}`}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '10px', color: 'var(--silver)', fontSize: '0.8rem' }}>
                <span>🔒 Secure SSL</span>
                <span>💳 PCI Compliant</span>
                <span>✓ Stripe Verified</span>
              </div>
              <p style={{ color: 'var(--dark-steel)', fontSize: '0.85rem' }}>
                Your payment information is encrypted and secure.
              </p>
            </div>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={() => navigate('/profile/restaurant')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--silver)',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'color 0.3s ease'
                }}
              >
                ← Cancel and return to profile
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(26, 26, 26, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="v2-spinner"></div>
        </div>
      )}
    </div>
  );
}

export default CheckoutRestaurantUnlimited;
